import { BCMSDate, BCMSCurrency, BCMSDefaultModel, BCMSRecord, BCMSStateManagerCRUD, useBCMSContext, useRoute, useRouteData, useStateManager, useWorkflow } from "@bcm-dev/bcms-vite";
import { PageContainer, PageContentView, WidgetContainer, WidgetView } from 'components/shared';
import { PrimaryButton, SecondaryButton } from 'components/ui';
import Classes, { Account, CustomItemTypeCode, PositivePayCheck, PositivePayCheckFile,
PositivePayCheckFileRow, PositivePayCheckTemplate, PositivePayImportedChecksUsingTemplateHistory } from "model";
import React, { useEffect, useState } from 'react';
import TASPositivePayCheckForm from "./classes/TASPositivePayCheckForm";
import TASPositivePayImportChecksUsingTemplateDesktopView from "./components/TASPositivePayImportChecksUsingTemplateDesktopView";
import TASPositivePayImportChecksUsingTemplateMobileView from "./components/TASPositivePayImportChecksUsingTemplateMobileView";
import TASPositivePayCheckFormUI from "./classes/TASPositivePayCheckFormUI";
import { SVGAsset } from "res";
import InfoAlertView from "components/shared/views/InfoAlertView";
import "./styles.css";
import "./mobile.css";

/**
 * @function TASPositivePayImportChecksUsingTemplatePage
 * @return {React.Component}
 * @exported true
 * @description The TAS Positive Pay Add Check page.
 */
export default function TASPositivePayImportChecksUsingTemplatePage(props) {
  const context = useBCMSContext();
  BCMSRecord.globalControllerFor(Classes, context);

  const data = { ...getDefaultData(), ...props.data ?? {} };
  const ui = { ...getDefaultUI(data.checkForms), ...props.ui ?? {} };

  const controller = (state, setState) => ({
    data: state.data,
    ui: state.ui,

    onDesktopChange: (id, prop) => val => {
      let checkForms = state.data.checkForms;
      const selectedCheckform = checkForms.filter(item => item.id === id)[0];

      checkForms = checkForms.reduce((acc, item) => {
        if(item.id === selectedCheckform.id) {
          const account = selectedCheckform.account ? selectedCheckform.account.id : undefined;
          
          acc.push(
            TASPositivePayCheckForm.init({ ...selectedCheckform.record, account: account, [prop]: val, })
          )
        } else {
          acc.push(item);
        }
        
        return acc;
      }, []);

      BCMSStateManagerCRUD.init(state)
      .update([ "data", "checkForms"], checkForms)
      .submit(setState);
    },

    onMobileChange: (id, prop) => val => {
      let checkForms = state.ui.checkForms;
      
      checkForms = checkForms.reduce((acc, item) => {
        if(item.id === id) {
          acc.push(TASPositivePayCheckFormUI.init({ ...item.record, [prop]: { isActive: val} }));
        } else {
          acc.push(item);
        }
        return acc;
      }, []);

      BCMSStateManagerCRUD.init(state)
      .update([ "ui", "checkForms"], checkForms)
      .submit(setState);
    },

    onMobileSave: (id, stateEditCheckForms) => val => {
      var editCheckForms = stateEditCheckForms;
      var findSelectedIndex = editCheckForms.findIndex(item => item.id === id);
      var selectedEditCheckForm = editCheckForms[findSelectedIndex];
      const currCheckFormRecord = { 
        ...selectedEditCheckForm.record,
         account: selectedEditCheckForm.account.id,
        mobileViewIsEditing: false 
      };     

      const defaultCheck = BCMSDefaultModel.positivePayCheck();
      const defaultCheckRecord = defaultCheck.record;
      const merge = BCMSDefaultModel.mergeRecords(currCheckFormRecord, defaultCheckRecord);

      const updatedEditCheckForm = TASPositivePayCheckForm.init(merge);

      const currCheckForms = state.data.checkForms;
      const filteredCheckForms = currCheckForms.filter(item => item.id !== id);

      const updatedCheckForms = [ 
        ...filteredCheckForms.slice(0, findSelectedIndex), 
        updatedEditCheckForm, 
        ...filteredCheckForms.slice(findSelectedIndex) 
      ];

      BCMSStateManagerCRUD.init(state)
      .update([ "data", "checkForms"], updatedCheckForms)
      .submit(setState);
    },

    onDeleteClick: id => evt => {
      const checkForms = state.data.checkForms;
      const filteredCheckForms = checkForms.filter(item => item.id !== id);

      BCMSStateManagerCRUD.init(state)
      .update([ "data", "checkForms"], filteredCheckForms)
      .submit(setState);
    },

    onSubmitClick: onSubmitCallback => evt => {
      const checkForms = state.data.checkForms;
      let submittedChecks = [];
      const itemTypeCodes = state.data.itemTypeCodes;

      let createdChecks = checkForms.reduce((acc, item) => {
        const defaultCheck = BCMSDefaultModel.positivePayCheck();
        const defaultCheckRecord = defaultCheck.record;
        const defaultAccount = Account.initAllBusinessAssetOnly().filter(item => item.type === "checking")[0];
        defaultCheckRecord.account = defaultAccount.id;
        item.type = itemTypeCodes.filter(_item => _item.itemTypeMappingValue === item.type)[0].itemTypeMapping;

        const merge = BCMSDefaultModel.mergeRecords(item.record, defaultCheckRecord);
        const updatedCheckForm = TASPositivePayCheckForm.init(merge);
        submittedChecks.push(updatedCheckForm);
        
        acc.push(
          PositivePayCheck.create({ ...updatedCheckForm.record, account: updatedCheckForm.account.id, amount: updatedCheckForm.amount.amount })
        );

        return acc;
      }, []);

      const history = PositivePayImportedChecksUsingTemplateHistory.init({
        id: PositivePayImportedChecksUsingTemplateHistory.nextID(),
        template: state.data.template.id,
        file: state.data.file.id,
        confirmationNumber: "39068",
        checks: submittedChecks.reduce((acc, item) => {
          acc.push(`${ item.id}`);
          return acc;
        }, [])
      });
      
      createdChecks.push(
        PositivePayImportedChecksUsingTemplateHistory.create(history)
      );

      BCMSRecord.batch(createdChecks, context, Classes);
      var currData = { state: { history } };
      onSubmitCallback(currData);
    },

    updateData: (templateID, positivePayCheckFileID) => {
      const data = getDefaultData(templateID, positivePayCheckFileID);

      BCMSStateManagerCRUD.init(state)
      .update([ "data", "itemTypeCodes" ], data.itemTypeCodes)
      .update([ "data", "checkForms" ], data.checkForms)
      .update([ "data", "defaultCheckForm" ], data.defaultCheckForm)
      .update([ "data", "accounts" ], data.accounts)
      .update([ "data", "selectedAccount" ], data.selectedAccount)
      .update([ "data", "isReviewSubmittedChecksEnabled" ], data.isReviewSubmittedChecksEnabled)
      .update([ "data", "isSubmitButtonEnabled" ], data.isSubmitButtonEnabled)
      .submit(setState);
    },

    getPageErrors: (prop) => {
      const checkForms = state.data.checkForms;
      
      return checkForms.reduce((acc, item) => {
        if(!item[prop]) {
          acc.push(item);
        }
        return acc;
      }, []);;
    }
  });

  return (
    <PageContainer className="tas-positive-pay-import-checks-using-template-page" state={ { data, ui } } controller={ controller }>
      <PageContent />
    </PageContainer>
  );
}

/**
 * @function getDefaultData
 * @param {string} templateID
 * @param {string} positivePayCheckFileID
 * @return {Object}
 * @description Get the default data for the "TAS Positive Pay Import Checks Using Template" page.
 */
export const getDefaultData = (templateID, positivePayCheckFileID) => {
  const accounts = Account.initAllBusinessAssetOnly().filter(item => item.type === "checking");
  const selectedAccount = accounts[0] ?? {};
  var defaultCheckForm = TASPositivePayCheckForm.init({ id: PositivePayCheck.nextID(), account: selectedAccount.id });

  const selectedFile = PositivePayCheckFile.initByID(positivePayCheckFileID ??"0");
  const selectedFileRows = PositivePayCheckFileRow.initAll().filter(item => item.positivePayCheckFile.id === selectedFile.id);
  const template = templateID ? PositivePayCheckTemplate.initByID(templateID) : PositivePayCheckTemplate.init({
    id: "0",
    mapColumn1: "payee",
    mapColumn2: "amount",
    mapColumn3: "accountNumber",
    mapColumn4: "itemTypeCode",
    mapColumn5: "accountType",
    mapColumn6: "referenceNumber",
    mapColumn7: "checkNumber",
    mapColumn8: "issuedDate"
  });

  const itemTypeCodes = [
    { id: "0", itemTypeMapping: "issue", itemTypeMappingValue: "I" },
    { id: "1", itemTypeMapping: "stop", itemTypeMappingValue: "S" },
    { id: "2", itemTypeMapping: "void", itemTypeMappingValue: "V" }
  ];

  let customItemTypeCodes = template.customItemTypeCodes ? template.customItemTypeCodes : [];
  
  customItemTypeCodes.map(item => {
    let code = CustomItemTypeCode.initByID(item);
    const filteredDefaultItemTypeCode = itemTypeCodes.filter(item => item.itemTypeMapping === code.itemTypeMapping)[0];
    filteredDefaultItemTypeCode.itemTypeMappingValue = code.itemTypeMappingValue;
  });

  const getTemplateColumnForValue = (template, valueToFind) => {
    return Object.keys(template.record)
    .filter(key => key.includes(`mapColumn`))
    .reduce((acc, key) => {
      if(template.record[key].includes(valueToFind)) {
        acc.push(key);
      }
      return acc;
    }, [])[0];
  };

  const checkForms = selectedFileRows.reduce((acc, item, index) => {
    const nextID = parseFloat(PositivePayCheck.nextID()) + index;
    const templateColumnForAccountNumber = getTemplateColumnForValue(template, "accountNumber");
    const templateColumnForCheckNumber = getTemplateColumnForValue(template, "checkNumber");
    const templateColumnForAmount = getTemplateColumnForValue(template, "amount");
    const templateColumnForPayee = getTemplateColumnForValue(template, "payee");
    const templateColumnForReferenceNumber = getTemplateColumnForValue(template, "referenceNumber");
    const templateColumnForType = getTemplateColumnForValue(template, "itemTypeCode");
    const templateColumnForIssuedDate = getTemplateColumnForValue(template, "issuedDate");

    const accountNumber = item[templateColumnForAccountNumber];
    const account = Account.initByID(accountNumber);
    const issuedDate = item[templateColumnForIssuedDate];
    const checkNumber = item[templateColumnForCheckNumber];
    let amount = item[templateColumnForAmount];
    // We want 0s to appear the amount inputs on this page, so "10.00" instead of "10".
    amount = amount ? BCMSCurrency.init(amount).format().slice(1) : undefined;
    const payee = item[templateColumnForPayee];
    const referenceNumber = item[templateColumnForReferenceNumber];
    const type = item[templateColumnForType];
    

    acc.push(
      TASPositivePayCheckForm.init({
        id: nextID, account: account ? account.id : undefined,
        issuedDate: issuedDate ? BCMSDate.init().offset(issuedDate) : undefined,
        checkNumber: checkNumber ? checkNumber : undefined,
        amount: amount ? BCMSCurrency.init(amount) : undefined,
        payee: payee ? payee : undefined,
        referenceNumber: referenceNumber ? referenceNumber : undefined,
        type: type ? type : undefined
      })
    );
    return acc;
  }, []);

  return {
    template,
    file: selectedFile,
    accounts: accounts,
    selectedAccount: selectedAccount,
    itemTypeCodes: itemTypeCodes,
    checkForms: checkForms,
    defaultCheckForm: defaultCheckForm,
    isReviewSubmittedChecksEnabled: false,
    isSubmitButtonEnabled: true
  }
};

/**
 * @function getDefaultUI
 * @param {Object} checkForms
 * @return {Object}
 * @description Get the default UI for the "TAS Positive Pay Import Checks Using Template" page.
 */
export const getDefaultUI = (checkForms) => {
  checkForms = checkForms ?? [];
  checkForms = checkForms.reduce((acc, item, index) => {
    acc.push(TASPositivePayCheckFormUI.init(
      { id: item.id, checkForm: item, isEditing: true, mobile: { isActive: index === 0 } }
    ));
    return acc;
  }, []);

  return {
    checkForms,
    accountSelect: { isActive: false },
    cancelModal: { isActive: false },
  };
};

/**
 * @function PageContent
 * @param {Object} props
 * @return {React.Component}
 * @description The content of the "TAS Positive Pay Import Checks Using Template" page.
 */
function PageContent(props) {
  const { data, ui, onSubmitClick, updateData, getPageErrors } = useStateManager();
  const hasAccountErrors = getPageErrors("account").length > 0;
  let { templateID = undefined, positivePayCheckFileID = undefined } = useRouteData().route ?? {};
  window.history.replaceState({}, document.title);
  const [ didModifyData, setDidModifyData ] = useState(false);
  const navigate = useRoute();

  const onCancelClick = evt => {
    navigate(`/tasPositivePayImportChecks`);
  };

  useEffect(() => {
    if(!didModifyData && templateID && positivePayCheckFileID) {
      updateData(templateID, positivePayCheckFileID);
      setDidModifyData(true);
    }
  }, []);

  const onSubmitCallback = data => {
    navigate(`/tasPositivePayImportChecks`, data);
  };

  return (
    <PageContentView>
         <WidgetContainer className="tas-positive-pay-add-check-widget">
            <WidgetView text="Import Checks">
              { hasAccountErrors
              && <div className="flex-container flex-horizontal error-alert-info-header-container">
              <InfoAlertView className="alert set-width" text="Required field(s) can't be blank." icon={ <SVGAsset id="AlertTriangleIcon" className="" /> } />
              <div className="flex-width"></div>
              </div> 
              }
              { <TASPositivePayImportChecksUsingTemplateDesktopView hasAccountErrors={ hasAccountErrors } edit={ { editCheckForms: data.editCheckForms } }
              cancelModal={ ui.cancelModal } /> }
              { <TASPositivePayImportChecksUsingTemplateMobileView edit={ { editCheckForms: data.editCheckForms } }
              cancelModal={ ui.cancelModal } /> }
              <div className="flex-container flex-horizontal buttons-container">
                <div className="set-width button-container secondary-button-container">
                    <SecondaryButton text="Cancel"
                    dataPoint={ `TAS_Positive_Pay_Import_Checks_Using_Template_Page_Cancel_Button`}
                    onClick={ onCancelClick } />
                </div>
                <div className="set-width button-container">
                  <PrimaryButton className="set-width" text="Save"
                  dataPoint={ `TAS_Positive_Pay_Import_Checks_Using_Template_Page_Save_Button`}
                  onClick={ onSubmitClick(onSubmitCallback) } />
                </div>
              </div>
            </WidgetView>
        </WidgetContainer>
    </PageContentView>
  );
}